import { useState } from 'react'
import { Alert, Button, Card, Descriptions, Empty, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EditOutlined, FileAddOutlined, PrinterOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { tenantToday } from '@/shared/lib/dayRange'
import { formatMoneyValue } from '@/shared/lib/money'
import { ContractCancelModal, ContractRenewModal } from './ContractDecisionModals'
import { ContractFormModal } from './ContractFormModal'
import { expiryState, fetchSubscriberContracts, type Contract } from './contractApi'
import { useContractPrinter } from './useContractPrinter'

const EXPIRY_COLOR = { expired: 'red', expiring: 'orange', ok: 'green', cancelled: 'default' } as const

export function ContractExpiryTag({ contract }: { contract: Contract }) {
  const { t } = useTranslation()
  const state = expiryState(contract, tenantToday().format('YYYY-MM-DD'))
  return <Tag color={EXPIRY_COLOR[state]}>{t(`contract.expiry.${state}`)}</Tag>
}

/** Abonent kartochkasidagi «Shartnoma» bo'limi (ADR-051). */
export function SubscriberContracts({ subscriberId }: { subscriberId: string }) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canManage = can('subscriber.contract.manage')
  const printer = useContractPrinter()
  const [editing, setEditing] = useState<Contract | 'new' | null>(null)
  const [renewing, setRenewing] = useState<Contract | null>(null)
  const [cancelling, setCancelling] = useState<Contract | null>(null)

  const contracts = useQuery({
    queryKey: queryKeys.subscribers.contracts(subscriberId),
    queryFn: ({ signal }) => fetchSubscriberContracts(subscriberId, signal),
  })
  const active = contracts.data?.find((contract) => contract.state === 'ACTIVE') ?? null
  const money = (contract: Contract, value: number | null | undefined) =>
    value == null ? '—' : formatMoneyValue({ amount: value, currency: contract.currency })

  const columns: ColumnsType<Contract> = [
    { key: 'number', title: t('contract.fields.number'), render: (_, row) => <Typography.Text code>{row.number}</Typography.Text> },
    { key: 'kind', title: t('contract.fields.kind'), render: (_, row) => t(`contract.kind.${row.kind}`) },
    { key: 'period', title: t('contract.fields.period'), render: (_, row) => `${row.startsOn} → ${row.endsOn}` },
    { key: 'state', title: t('contract.fields.state'), render: (_, row) => <ContractExpiryTag contract={row} /> },
    {
      key: 'cancel',
      title: t('contract.cancelReason'),
      render: (_, row) => row.cancelReason ?? '—',
    },
  ]

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {contracts.isError ? <Alert type="error" showIcon message={translateError(contracts.error, t)} /> : null}
      <Card
        loading={contracts.isPending}
        title={active ? t('contract.activeTitle', { number: active.number }) : t('contract.noActive')}
        extra={
          canManage ? (
            active ? (
              <Space wrap>
                <Button icon={<EditOutlined />} onClick={() => setEditing(active)}>
                  {t('common.edit')}
                </Button>
                <Button icon={<SyncOutlined />} onClick={() => setRenewing(active)}>
                  {t('contract.renew')}
                </Button>
                <Button danger icon={<StopOutlined />} onClick={() => setCancelling(active)}>
                  {t('contract.cancel')}
                </Button>
              </Space>
            ) : (
              <Button type="primary" icon={<FileAddOutlined />} onClick={() => setEditing('new')}>
                {t('contract.create')}
              </Button>
            )
          ) : null
        }
      >
        {active ? (
          <>
            <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }} bordered>
              <Descriptions.Item label={t('contract.fields.kind')}>{t(`contract.kind.${active.kind}`)}</Descriptions.Item>
              <Descriptions.Item label={t('contract.fields.period')}>
                {active.startsOn} → {active.endsOn} <ContractExpiryTag contract={active} />
              </Descriptions.Item>
              <Descriptions.Item label={t('contract.fields.termYears')}>{t('contract.years', { count: active.termYears })}</Descriptions.Item>
              {active.kind === 'GUARANTEE' ? (
                <Descriptions.Item label={t('contract.fields.depositAmount')}>{money(active, active.depositAmount)}</Descriptions.Item>
              ) : null}
              {active.kind === 'RENTAL' ? (
                <Descriptions.Item label={t('contract.fields.monthlyRental')}>{money(active, active.monthlyRental)}</Descriptions.Item>
              ) : null}
              {active.kind === 'RENTAL' && active.rentalNextOn ? (
                <Descriptions.Item label={t('contract.fields.rentalNextOn')}>{active.rentalNextOn}</Descriptions.Item>
              ) : null}
              <Descriptions.Item label={t('contract.fields.installationTotal')}>{money(active, active.installationTotal)}</Descriptions.Item>
              <Descriptions.Item label={t('contract.fields.paidAmount')}>{money(active, active.paidAmount)}</Descriptions.Item>
              <Descriptions.Item label={t('contract.fields.remainingAmount')}>{money(active, active.remainingAmount)}</Descriptions.Item>
              <Descriptions.Item label={t('contract.fields.deviceMac')}>{active.deviceMac ?? '—'}</Descriptions.Item>
              <Descriptions.Item label={t('contract.fields.guarantorName')}>{active.guarantorName ?? '—'}</Descriptions.Item>
            </Descriptions>
            <Button
              style={{ marginTop: 12 }}
              icon={<PrinterOutlined />}
              loading={printer.loading}
              onClick={() => printer.print(active)}
            >
              {t('contract.print.button')}
            </Button>
          </>
        ) : (
          <Empty description={t('contract.noActiveHint')} />
        )}
      </Card>

      {(contracts.data?.length ?? 0) > 1 ? (
        <Card title={t('contract.history')} size="small">
          <Table<Contract>
            size="small"
            rowKey={(row) => row.id}
            columns={columns}
            dataSource={contracts.data ?? []}
            pagination={false}
          />
        </Card>
      ) : null}

      <ContractFormModal
        open={editing !== null}
        subscriberId={subscriberId}
        contract={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
      <ContractRenewModal contract={renewing} onClose={() => setRenewing(null)} />
      <ContractCancelModal contract={cancelling} onClose={() => setCancelling(null)} />
      {printer.portal}
    </Space>
  )
}
